import { DeliveryProviderPortalService } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/';

import { objectsEqual } from '../../utils/utils';
import { DAY_LAST_MINUTE, weekDays } from './constants';
import { ScheduleDayType, ScheduleWeek } from './types';

const DAY_MAMP_FOR_WEEK_WITH_SUNDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

export const convertMinutesToTimeString = (timestamp: number) => {
  const hrs = Math.floor(timestamp / 60);
  const mins = timestamp - hrs * 60;

  return `${hrs > 9 ? hrs : `0${hrs}`}:${mins > 9 ? mins : `0${mins}`}`;
};

export const convertTimeStringToMinutes = (str: string) => {
  const date = new Date(`1970-01-01T${str}`);

  return date.getMinutes() + date.getHours() * 60;
};

export function convertScheduleIn(
  schedule: DeliveryProviderPortalService.ScheduleV0,
): ScheduleWeek {
  const scheduleIn: ScheduleWeek = DAY_MAMP_FOR_WEEK_WITH_SUNDAY_FIRST.map((index) => {
    const scheduleDay = schedule[index] || [];

    return scheduleDay.map((interval) => [
      interval.start_at_minutes_from_midnight,
      interval.stop_at_minutes_from_midnight,
    ]);
  });

  scheduleIn.forEach((day, index) => {
    const lastInterval = day
      // find all intervals, that end at 23:59
      .filter((interval) => interval[1] === DAY_LAST_MINUTE)
      // take only one of them, the longest lasting one
      .sort((a, b) => b[0] - a[0])[0];

    if (lastInterval) {
      const nextDay = scheduleIn[(index + 1) % scheduleIn.length];
      const nextDayFirstInterval = nextDay
        // find all intervals, that start at 00:00, but do not last longer than 12:00
        // or longer than prev. day's starting interval, to avoid cases like `02:00 - 23:59` / `00:00 - 05:00`,
        // to become `02:00 - 05:00`
        .filter(
          (interval) =>
            interval[0] === 0 &&
            interval[1] < DAY_LAST_MINUTE * 0.5 &&
            interval[1] < lastInterval[0],
        )
        // take only one of them, the longest lasting one
        .sort((a, b) => b[1] - a[1])[0];

      if (nextDayFirstInterval) {
        // change [..., 23:59] intervals end time to end time of nextDayFirstInterval
        // eslint-disable-next-line prefer-destructuring
        lastInterval[1] = nextDayFirstInterval[1];
        // remove nextDayFirstInterval from nextDay
        nextDay.splice(nextDay.indexOf(nextDayFirstInterval), 1);
      }
    }
  });

  return scheduleIn;
}
/**
 * Takes schedule with various possible mistakes, overlaps etc.
 * Outputs normalized format that is acceptable by backend
 *
 *
 * @param schedule
 */
export function normalizeSchedule(schedule: ScheduleWeek): ScheduleWeek {
  const newValue: ScheduleWeek = [];

  weekDays.forEach((index) => {
    newValue[index] = newValue[index] || [];
    const dayIntervals = schedule[index] || [];

    dayIntervals.forEach(([start, stop]) => {
      if (start < stop) {
        // Normal time like 09:00 - 21:00
        newValue[index].push([start, stop]);
      } else if (start === stop) {
        // Zero period time, like 09:00 - 09:00
      } else {
        // Till the end of day (09:00 - 00:00) or to the next day (09:00 - 03:00)

        // To current day add (start - 23:59)
        newValue[index].push([start, DAY_LAST_MINUTE]);

        // Check if goes to next day
        if (stop > 0) {
          // Support for Monday to be Sunday's next day
          const nextDayIndex = (index + 1) % weekDays.length;

          // Gently update next day. If existed then push, if not then create a times
          if (!newValue[nextDayIndex]) {
            newValue[nextDayIndex] = [];
          }
          newValue[nextDayIndex].push([0, stop]);
        }
      }
    });
  });
  return newValue;
}

export function convertScheduleOut(
  schedule: ScheduleWeek,
): DeliveryProviderPortalService.ScheduleV0 {
  const normalizedSchedule = normalizeSchedule(schedule);

  return weekDays.reduce((mem, index) => {
    const scheduleDay = normalizedSchedule[index];

    return {
      ...mem,
      [DAY_MAMP_FOR_WEEK_WITH_SUNDAY_FIRST[index]]: scheduleDay
        ? scheduleDay.map(([start, stop]) => ({
            start_at_minutes_from_midnight: start,
            stop_at_minutes_from_midnight: stop,
          }))
        : [],
    };
  }, {} as DeliveryProviderPortalService.ScheduleV0);
}

export function sortSchedule(schedule: ScheduleWeek): ScheduleWeek {
  return schedule.map((day) => {
    return day.sort(([start1], [start2]) => start1 - start2);
  });
}

export function checkIfDayHasConflicts(day: ScheduleDayType): boolean {
  return (
    day.length > 1 &&
    day.some(([start1, stop1], i1) =>
      day.some(([start2, stop2], i2) => i1 !== i2 && start1 <= stop2 && start2 <= stop1),
    )
  );
}

export function isScheduleEmpty(schedule: DeliveryProviderPortalService.ScheduleV0): boolean {
  if (!schedule) {
    return true;
  }

  for (const day of Object.keys(schedule)) {
    if (schedule[day].length > 0) {
      return false;
    }
  }
  return true;
}

export function isScheduleValid(schedule: ScheduleWeek): boolean {
  const isEmpty = !weekDays.some((day) => schedule[day].length > 0);
  if (isEmpty) {
    return false;
  }
  for (const day of weekDays) {
    const intervals = schedule[day] || [];
    const hasConflicts = checkIfDayHasConflicts(intervals);
    if (hasConflicts) {
      return false;
    }
  }
  return true;
}

// TODO refactor this to return a more explicit interface use lodash groupBy.
export function groupSimilarTimesByDay(
  scheduleStartingWithMonday: DeliveryProviderPortalService.ScheduleV0,
): number[][] {
  const groups: Set<number[]> = new Set();
  const addedDays: Set<number> = new Set();

  for (const day of weekDays) {
    if (scheduleStartingWithMonday[day].length) {
      const similarDays: number[] = [];
      if (!addedDays.has(day)) {
        similarDays.push(day);
        addedDays.add(day);
        const daysToCheck = weekDays.slice(Number(day) + 1);
        for (const otherDay of daysToCheck) {
          if (objectsEqual(scheduleStartingWithMonday[day], scheduleStartingWithMonday[otherDay])) {
            similarDays.push(otherDay);
            addedDays.add(otherDay);
          }
        }
        groups.add(similarDays);
      }
    }
  }
  return Array.from(groups);
}
